/**
 * Reusable profile page layout components for sensnet detail pages.
 *
 * Components:
 *  - ProfileHeader   — dark navy hero bar (title, subtitle, badge, optional back button + actions)
 *  - ProfileSection  — section wrapper with amber or dark header bar + children
 *  - ProfileFooter   — dark navy Trilogy branding footer
 *
 * Usage:
 *  <ProfileHeader
 *    title="Kindred Water Tower"
 *    subtitle="Activated: March 15, 2025"
 *    badge="Active"
 *    backButton={<Link to="..."><Button variant="ghost" size="sm" className="gap-2 text-white hover:bg-white/10 hover:text-white"><ArrowLeft className="h-4 w-4" />Back</Button></Link>}
 *    actions={<Button variant="outline" size="sm">Download PDF</Button>}
 *  />
 *  <ProfileSection title="Location Information" icon={<MapPin />} variant="amber">
 *    ...content...
 *  </ProfileSection>
 */

import type { ReactNode } from 'react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

// ── ProfileHeader ──────────────────────────────────────────────────────────

interface ProfileHeaderProps {
  title: string
  /** Optional line below the title (e.g. "Activated: March 15, 2025") */
  subtitle?: string
  /** Optional badge text rendered next to the subtitle (e.g. "Active") */
  badge?: string
  /** Optional icon rendered before the subtitle */
  subtitleIcon?: ReactNode
  /** Optional back button rendered top-left inside the dark banner */
  backButton?: ReactNode
  /** Optional action buttons rendered top-right inside the dark banner */
  actions?: ReactNode
}

export function ProfileHeader({ title, subtitle, badge, subtitleIcon, backButton, actions }: ProfileHeaderProps) {
  return (
    <div className="rounded-lg bg-slate-700 px-6 py-4 text-white shadow-sm">
      {(backButton || actions) && (
        <div className="mb-3 flex items-center justify-between">
          <div>{backButton}</div>
          <div>{actions}</div>
        </div>
      )}
      <h1 className="text-xl font-bold">{title}</h1>
      {(subtitle || badge) && (
        <div className="mt-1 flex items-center gap-2 text-sm text-slate-300">
          {subtitleIcon}
          {subtitle && <span>{subtitle}</span>}
          {badge && (
            <Badge className="bg-green-500 text-white hover:bg-green-500 text-xs">
              {badge}
            </Badge>
          )}
        </div>
      )}
    </div>
  )
}

// ── ProfileSection ─────────────────────────────────────────────────────────

interface ProfileSectionProps {
  /** Section title text */
  title: string
  /** Icon element shown before the title */
  icon?: ReactNode
  /** Icon tile accent: 'amber' = golden, 'dark' = slate (default: 'amber') */
  variant?: 'amber' | 'dark'
  children: ReactNode
  /** Extra className applied to the outer wrapper */
  className?: string
  /** Optional element rendered at the top-right of the header bar (e.g. an action button) */
  action?: ReactNode
}

export function ProfileSection({
  title,
  icon,
  variant = 'amber',
  children,
  className = '',
  action,
}: ProfileSectionProps) {
  // The variant only tints the icon tile; the card itself stays light.
  const iconCls =
    variant === 'dark'
      ? 'bg-slate-100 text-slate-600'
      : 'bg-amber-100 text-amber-700'

  return (
    <section className={cn('overflow-hidden rounded-xl border border-border bg-card shadow-sm', className)}>
      <div className="flex min-h-14 shrink-0 items-center gap-3 border-b border-border px-5 py-3">
        {icon && (
          <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', iconCls)}>
            {icon}
          </span>
        )}
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {action && <div className="ml-auto flex items-center gap-2">{action}</div>}
      </div>
      {children}
    </section>
  )
}

// ── ProfileFooter ──────────────────────────────────────────────────────────

export function ProfileFooter() {
  return (
    <div className="rounded-lg bg-slate-700 px-6 py-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-400">
            <span className="text-xs font-bold text-slate-800">T</span>
          </div>
          <span className="text-xs font-semibold tracking-widest text-white uppercase">
            Let's Build It Together
          </span>
        </div>
        <span className="text-xs tracking-widest text-slate-400 uppercase">
          www.trilogynet.com
        </span>
      </div>
    </div>
  )
}
